import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import {
  calcSmartSlotsV3,
  buildDynamicServiceBuckets,
  getIndustryRule,
  timeToMinutes,
  minutesToTime,
} from '@/lib/smartSlots';
import type { ScoredSlot, ServiceBucket } from '@/lib/smartSlots';
import { logger } from '../lib/logger';

import {
  calculateEarliestBookableTime,
  localDateString,
  refreshBookingGuardrailContext,
  timeStringToMinutes,
  type BookingGuardrailSettings,
} from '@/lib/booking/bookingGuardrails';
import {
  createAnchor,
  isAnchorStale,
  readMonotonic,
  resolveNow,
  type ServerClockAnchor,
} from '@/lib/booking/serverClock';

export interface PublicSalon {
  id: string;
  organization_id: string | null;
  name: string;
  phone?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  place_id?: string | null;
  currency?: string | null;
  locale?: string | null;
  country_code?: string | null;
  phone_prefix?: string | null;
  logo_url?: string | null;
  cover_photo_url?: string | null;
  gallery_urls?: string[] | null;
  salon_code: string;
  opening_hours: Record<string, { open: string | null; close: string | null }>;
  settings?: Record<string, unknown>;
  industry_type?: string | null;
  /** Timezone del negocio (organizations.timezone). Nunca el del navegador. */
  timezone: string;
  /** Guardrails de agenda online (organization_booking_settings). */
  guardrails: BookingGuardrailSettings;
}

export interface PublicService {
  id: string;
  name: string;
  description?: string | null;
  category: string;
  duration_minutes: number;
  price: number;
  is_active: boolean;
  is_bookable_online: boolean;
  image_url?: string | null;
}

export interface PublicProfessional {
  id: string;
  full_name: string;
  avatar_url?: string | null;
  color?: string | null;
  job_title?: string | null;
  is_active: boolean;
  is_bookable_online: boolean;
  service_ids: string[];
  average_rating?: number | null;
  total_reviews?: number;
  notes?: string | null;
}

export interface ClientHistory {
  clientName: string;
  preferred_professional_id: string | null;
  preferred_hour_range: { from: number; to: number } | null;
  total_visits: number;
  no_show_count: number;
  last_visit_date: Date | null;
  service_visit_counts: Record<string, number>;
}

export interface BookingParams {
  selectedServices: { id: string; duration_minutes: number }[];
  professionalId: string;
  date: Date;
  startMinute: number;
  clientName: string;
  clientPhone: string;
  clientEmail?: string;
  depositAmount: number;
  depositPaid?: boolean;
  reservationId?: string | null;
  /** true cuando MercadoPago ya confirmó el pago del depósito */
  mpPaymentDone?: boolean;
  /** collection_id devuelto por MercadoPago en el redirect */
  mpPaymentId?: string | null;
  /** código de campaña que el cliente escribió (opcional) */
  campaignCode?: string | null;
}

const DAY_MAP: Record<number, string> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

function dateToMinutes(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );
}

export function usePublicBooking(salonCode: string) {
  const [salon, setSalon] = useState<PublicSalon | null>(null);
  const [services, setServices] = useState<PublicService[]>([]);
  const [professionals, setProfessionals] = useState<PublicProfessional[]>([]);
  const [serviceBuckets, setServiceBuckets] = useState<ServiceBucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slotsStale, setSlotsStale] = useState(false);
  // Ancla de reloj del servidor. Ref y no estado: cambiarla no debe repintar,
  // y fetchSlots la lee de forma síncrona.
  const serverClockRef = useRef<ServerClockAnchor | null>(null);
  // BOOKING_TOO_SOON invalida settings + reloj en conjunto. Mientras este
  // flag siga activo, getAvailableSlots falla cerrado y nunca reutiliza los
  // guardrails que el servidor acaba de demostrar obsoletos.
  const guardrailRefreshRequiredRef = useRef(false);

  const salonRef = useRef<PublicSalon | null>(null);
  const serviceBucketsRef = useRef<ServiceBucket[]>([]);

  useEffect(() => {
    salonRef.current = salon;
  }, [salon]);

  useEffect(() => {
    serviceBucketsRef.current = serviceBuckets;
  }, [serviceBuckets]);

  // Initial data load
  useEffect(() => {
    if (!salonCode) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        // 1. Salon
        const { data: salonData, error: salonError } = await supabase
          .from('salons')
          .select('id, organization_id, name, phone, address, latitude, longitude, place_id, currency, locale, country_code, phone_prefix, logo_url, cover_photo_url, gallery_urls, salon_code, opening_hours, settings, industry_type')
          .eq('salon_code', salonCode)
          .maybeSingle();

        if (salonError) throw salonError;
        if (salonData && !salonData.organization_id) {
          if (!cancelled) setError('Salón sin organización configurada');
          return;
        }
        if (!salonData) {
          if (!cancelled) setError('Salón no encontrado');
          return;
        }
        // Timezone y guardrails son ORGANIZATION-owned: el reloj del
        // navegador del cliente no es fuente de verdad (puede estar en otro
        // huso, o mal puesto).
        const [{ data: orgRow }, { data: guardrailRow }] = await Promise.all([
          supabase
            .from('organizations')
            .select('timezone')
            .eq('id', salonData.organization_id)
            .maybeSingle(),
          supabase
            .from('organization_booking_settings')
            .select('minimum_notice_minutes, max_days_ahead, opening_protection_enabled, opening_protection_start_minutes, opening_protection_end_minutes, opening_protection_buffer_minutes')
            .eq('organization_id', salonData.organization_id)
            .maybeSingle(),
        ]);

        const hydrated: PublicSalon = {
          ...(salonData as PublicSalon),
          timezone: orgRow?.timezone || 'America/Santiago',
          guardrails: {
            minimumNoticeMinutes: guardrailRow?.minimum_notice_minutes ?? 120,
            maxDaysAhead: guardrailRow?.max_days_ahead ?? 30,
            protectionEnabled: guardrailRow?.opening_protection_enabled ?? false,
            protectionStartMinutes: guardrailRow?.opening_protection_start_minutes ?? 0,
            protectionEndMinutes: guardrailRow?.opening_protection_end_minutes ?? 480,
            protectionBufferMinutes: guardrailRow?.opening_protection_buffer_minutes ?? 60,
          },
        };

        if (cancelled) return;
        setSalon(hydrated);
        salonRef.current = hydrated;

        // 2. Services
        const { data: svcsData, error: svcError } = await supabase
          .from('services')
          .select('id, name, description, category, duration_minutes, price, is_active, is_bookable_online, image_url')
          .eq('organization_id', salonData.organization_id)
          .filter('salon_id', 'eq', salonData.id)
          .eq('is_active', true)
          .eq('is_bookable_online', true)
          .order('category')
          .order('name');

        if (svcError) throw svcError;
        if (cancelled) return;
        const svcs = (svcsData ?? []) as PublicService[];
        setServices(svcs);
        const buckets = buildDynamicServiceBuckets(svcs);
        setServiceBuckets(buckets);
        serviceBucketsRef.current = buckets;

        // 3. Professionals
        const { data: rawPros, error: prosError } = await supabase
          .from('professionals')
          .select('id, full_name, avatar_url, color, job_title, is_active, is_bookable_online, average_rating, total_reviews, notes')
          .eq('organization_id', salonData.organization_id)
          .filter('salon_id', 'eq', salonData.id)
          .eq('is_active', true)
          .eq('is_bookable_online', true)
          .order('display_order', { ascending: true, nullsFirst: false });

        if (prosError) throw prosError;
        if (cancelled) return;

        const professionalIds = (rawPros ?? [])
          .map((professional) => professional.id as string)
          .filter(Boolean);
        const serviceIds = svcs.map((service) => service.id);

        // 4. Professional ↔ service mapping
        let proServices: { professional_id: string; service_id: string }[] | null = [];
        if (professionalIds.length > 0 && serviceIds.length > 0) {
          const { data, error: psError } = await supabase
            .from('professional_services')
            .select('professional_id, service_id')
            .in('professional_id', professionalIds)
            .in('service_id', serviceIds);

          if (psError) throw psError;
          proServices = data;
        }
        if (cancelled) return;

        const psMap = (proServices ?? []).reduce<Record<string, string[]>>((acc, ps) => {
          if (!acc[ps.professional_id]) acc[ps.professional_id] = [];
          acc[ps.professional_id].push(ps.service_id);
          return acc;
        }, {});

        const mappedPros = (rawPros ?? []).map((p: Record<string, unknown>) => ({
          ...(p as PublicProfessional),
          service_ids: psMap[(p.id as string)] ?? [],
        })) as PublicProfessional[];

        if (!cancelled) setProfessionals(mappedPros);
      } catch (err: unknown) {
        if (!cancelled) {
          setError('Error al cargar el salón. Intenta de nuevo.');
          logger.error('[usePublicBooking]', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [salonCode]);

  // Realtime subscription
  useEffect(() => {
    if (!salon?.id || !salon.organization_id) return;

    const channel = supabase
      .channel(`public-booking-${salon.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'appointments', filter: `organization_id=eq.${salon.organization_id}` },
        () => setSlotsStale(true)
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'blocked_times', filter: `organization_id=eq.${salon.organization_id}` },
        () => setSlotsStale(true)
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'slot_reservations', filter: `organization_id=eq.${salon.organization_id}` },
        () => setSlotsStale(true)
      )
      .subscribe();

    return () => { void supabase.removeChannel(channel); };
  }, [salon?.id, salon?.organization_id]);

  const refreshGuardrailsAndServerClock = useCallback(async (): Promise<boolean> => {
    const currentSalon = salonRef.current;
    if (!currentSalon?.organization_id) return false;

    guardrailRefreshRequiredRef.current = true;
    serverClockRef.current = null;

    try {
      const refreshed = await refreshBookingGuardrailContext({
        loadSettings: async () => {
          const { data, error: settingsError } = await supabase
            .from('organization_booking_settings')
            .select('minimum_notice_minutes, max_days_ahead, opening_protection_enabled, opening_protection_start_minutes, opening_protection_end_minutes, opening_protection_buffer_minutes')
            .eq('organization_id', currentSalon.organization_id)
            .maybeSingle();

          if (settingsError) throw settingsError;
          return {
            minimumNoticeMinutes: data?.minimum_notice_minutes ?? 120,
            maxDaysAhead: data?.max_days_ahead ?? 30,
            protectionEnabled: data?.opening_protection_enabled ?? false,
            protectionStartMinutes: data?.opening_protection_start_minutes ?? 0,
            protectionEndMinutes: data?.opening_protection_end_minutes ?? 480,
            protectionBufferMinutes: data?.opening_protection_buffer_minutes ?? 60,
          };
        },
        loadServerNow: async () => {
          const { data, error: clockError } = await supabase.rpc('booking_server_now');
          if (clockError || !data) throw clockError ?? new Error('Booking server clock unavailable');
          return new Date(data as string);
        },
      });

      const refreshedSalon = { ...currentSalon, guardrails: refreshed.settings };
      salonRef.current = refreshedSalon;
      setSalon((previous) => previous?.id === refreshedSalon.id ? refreshedSalon : previous);
      serverClockRef.current = createAnchor(refreshed.serverNow, readMonotonic());
      guardrailRefreshRequiredRef.current = false;
      return true;
    } catch (refreshError) {
      logger.error('[usePublicBooking] booking_guardrails_refresh_failed', refreshError);
      return false;
    }
  }, []);

  const getAvailableSlots = useCallback(async (
    professionalId: string,
    date: Date,
    totalDuration: number,
    options?: {
      /** D4: client's preferred time range — boosts historically-used hours */
      clientPreferredRange?: { from: number; to: number } | null;
      /** Excludes active holds belonging to the same client phone. */
      clientPhone?: string | null;
    }
  ): Promise<{ slots: ScoredSlot[]; dayFull: boolean; clockUnavailable?: boolean }> => {
    setSlotsStale(false);
    if (guardrailRefreshRequiredRef.current && !(await refreshGuardrailsAndServerClock())) {
      return { slots: [], dayFull: false, clockUnavailable: true };
    }

    const currentSalon = salonRef.current;
    if (!currentSalon) return { slots: [], dayFull: false };

    // Hora autoritativa. Se refresca cuando el ancla envejece; entre refrescos
    // el tiempo avanza con el reloj monotónico, no con el calendario del
    // dispositivo.
    if (isAnchorStale(serverClockRef.current, readMonotonic())) {
      const { data: serverNowRaw, error: clockError } = await supabase.rpc('booking_server_now');
      if (clockError || !serverNowRaw) {
        // FAIL SAFE: sin hora autoritativa no se puede saber qué slots son
        // elegibles. Se devuelve vacío en lugar de caer al reloj del
        // dispositivo, que es justo el vector que esto cierra.
        // `clockUnavailable` distingue esto de "el día no tiene cupo": son
        // dos situaciones distintas y el cliente merece saber cuál es la suya.
        serverClockRef.current = null;
        return { slots: [], dayFull: false, clockUnavailable: true };
      }
      serverClockRef.current = createAnchor(new Date(serverNowRaw as string), readMonotonic());
    }

    const now = resolveNow(serverClockRef.current, readMonotonic());
    if (!now) return { slots: [], dayFull: false, clockUnavailable: true };

    const dayOfWeek = date.getDay();
    const dayName = DAY_MAP[dayOfWeek];
    // TODO ORG-CANON: mover la fuente de horarios a organization_locations.
    // Se lee de salons.opening_hours porque en la Producción ACTUAL el horario
    // no existe en ninguna otra tabla. Es una lectura de compatibilidad: no se
    // escriben horarios en salons ni se le añaden columnas. Retirar cuando
    // 003B/003C publiquen organization_locations.opening_hours.
    const hours = currentSalon.opening_hours?.[dayName];
    if (!hours?.open || !hours?.close) return { slots: [], dayFull: false };

    const { data: schedule } = await supabase
      .from('professional_schedules')
      .select('start_time, end_time, is_working, break_start, break_end')
      .eq('professional_id', professionalId)
      .eq('day_of_week', dayOfWeek)
      .eq('is_working', true)
      .maybeSingle();

    if (!schedule) return { slots: [], dayFull: false };

    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    // ISO date string for unavailability range query (YYYY-MM-DD)
    const dateStr = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-');

    // Fase 3: resolve effective buffer (max of salon setting vs industry minimum)
    const industryRule = getIndustryRule(currentSalon.industry_type);

    const [
      { data: appointments },
      { data: blocks },
      { data: unavail },
      { data: weekLoad },
      { data: demandStats },
      { data: activeHolds },
    ] = await Promise.all([
      // 'blocked' incluido a propósito: los horarios que el tenant bloquea
      // desde la agenda (AppointmentCalendar.tsx → performBlockTime) se
      // guardan como filas de `appointments` con status='blocked', NO en la
      // tabla `blocked_times` (esa tabla no tiene ningún escritor en toda la
      // app -- la query de abajo que la consulta siempre devuelve vacío,
      // legacy sin usar). Sin 'blocked' acá, la reserva pública mostraba esos
      // horarios como libres pese a estar bloqueados.
      supabase
        .from('appointments')
        .select('start_time, end_time, status')
        .eq('organization_id', currentSalon.organization_id)
        .filter('salon_id', 'eq', currentSalon.id)
        .eq('professional_id', professionalId)
        .in('status', ['scheduled', 'confirmed', 'in_progress', 'blocked'])
        .gte('start_time', dayStart.toISOString())
        .lte('start_time', dayEnd.toISOString()),
      supabase
        .from('blocked_times')
        .select('start_time, end_time')
        .eq('organization_id', currentSalon.organization_id)
        .filter('salon_id', 'eq', currentSalon.id)
        .eq('professional_id', professionalId)
        .gte('start_time', dayStart.toISOString())
        .lte('start_time', dayEnd.toISOString()),
      // V3 Fase 1: respect professional vacations / absences
      supabase
        .from('professional_unavailability')
        .select('start_datetime, end_datetime')
        .eq('organization_id', currentSalon.organization_id)
        .eq('professional_id', professionalId)
        .lt('start_datetime', dayEnd.toISOString())
        .gt('end_datetime', dayStart.toISOString()),
      // V3 Fase 2 D3: professional's weekly load in minutes
      supabase.rpc('get_professional_weekly_load', {
        p_professional_id: professionalId,
      }),
      // V3 Fase 2 D5: historical fill rates by hour slot
      supabase.rpc('get_slot_demand_for_scoring', {
        p_professional_id: professionalId,
        p_day_of_week: dayOfWeek,
      }),
      supabase.rpc('get_public_slot_holds_v2', {
        p_salon_id: currentSalon.id,
        p_professional_id: professionalId,
        p_requested_date: dateStr,
        p_client_phone: options?.clientPhone?.trim() || null,
      }),
    ]);

    // All-day unavailability → no slots at all
    const unavailRows = (unavail ?? []) as { start_datetime: string; end_datetime: string }[];

    // Fase 3: max_patients_day cap — if professional already has max appointments today, day is full
    // (un bloqueo administrativo no es un paciente, no debe contar para este tope)
    const appointmentCount = (appointments ?? []).filter((a) => a.status !== 'blocked').length;
    if (industryRule.max_patients_day !== null && appointmentCount >= industryRule.max_patients_day) {
      return { slots: [], dayFull: true };
    }

    const booked = [
      ...(appointments ?? []).map((a) => ({
        start: dateToMinutes(new Date(a.start_time)),
        end: dateToMinutes(new Date(a.end_time)),
      })),
      ...(blocks ?? []).map((b) => ({
        start: dateToMinutes(new Date(b.start_time)),
        end: dateToMinutes(new Date(b.end_time)),
      })),
      ...((activeHolds ?? []) as { slot_start_min: number; slot_end_min: number | null }[]).map((hold) => ({
        start: hold.slot_start_min,
        end: hold.slot_end_min ?? hold.slot_start_min + 15,
      })),
      // Professional unavailability blocks
      ...unavailRows
        .map((u) => ({
          start: dateToMinutes(new Date(u.start_datetime)),
          end: dateToMinutes(new Date(u.end_datetime)),
        })),
    ];

    // Build demand map for D5 scoring
    const slotDemandMap = new Map<number, number>();
    for (const row of (demandStats ?? []) as { hour_slot: number; fill_rate: number | null }[]) {
      if (row.fill_rate !== null) slotDemandMap.set(row.hour_slot, row.fill_rate);
    }

    const allSlots = calcSmartSlotsV3({
      apertura: timeToMinutes(schedule.start_time as string),
      cierre: timeToMinutes(schedule.end_time as string),
      breakStart: schedule.break_start ? timeToMinutes(schedule.break_start as string) : null,
      breakEnd: schedule.break_end ? timeToMinutes(schedule.break_end as string) : null,
      totalDur: totalDuration,
      booked,
      serviceBuckets: serviceBucketsRef.current,
      // V3 dimensions
      weeklyLoadMin: typeof weekLoad === 'number' ? weekLoad : undefined,
      clientPreferredRange: options?.clientPreferredRange ?? null,
      slotDemandMap,
      industryType: currentSalon.industry_type,
    });

    // ── BOOKING GUARDRAILS ────────────────────────────────────────────────
    // Último filtro del pipeline: se aplica DESPUÉS de horario, disponibilidad
    // del profesional, del servicio y de los conflictos existentes, y no
    // modifica ninguna de esas reglas -- solo recorta por abajo.
    //
    // Antes esto era `slot.startMin > dateToMinutes(new Date())`, que además
    // de ignorar la anticipación mínima usaba la hora del NAVEGADOR: un
    // cliente en otro huso veía slots que el negocio no podía atender.
    const targetDate = localDateString(date, currentSalon.timezone);
    const { guardrails } = currentSalon;

    const guardrail = calculateEarliestBookableTime({
      now,
      targetDate,
      openingMinutes: timeStringToMinutes(hours.open),
      minimumNoticeMinutes: guardrails.minimumNoticeMinutes,
      protectionEnabled: guardrails.protectionEnabled,
      protectionWindow: guardrails.protectionEnabled
        ? {
          startMinutes: guardrails.protectionStartMinutes,
          endMinutes: guardrails.protectionEndMinutes,
        }
        : null,
      protectionBufferMinutes: guardrails.protectionBufferMinutes,
      timezone: currentSalon.timezone,
    });

    const slots = guardrail.earliestMinutes === null
      ? []
      : allSlots.filter((slot) => slot.startMin >= guardrail.earliestMinutes!);

    // dayFull = professional has schedule but zero slots remain
    return { slots, dayFull: allSlots.length > 0 && slots.length === 0 ? false : slots.length === 0 };
  }, [refreshGuardrailsAndServerClock]);

  /**
   * V3: Look up a returning client by phone number.
   * Returns ClientHistory (preferred pro, hour range, visit count) or null if new client.
   */
  const lookupClientHistory = useCallback(async (phone: string): Promise<ClientHistory | null> => {
    const currentSalon = salonRef.current;
    if (!currentSalon || phone.trim().length < 7) return null;
    try {
      const { data, error } = await supabase.rpc('lookup_public_booking_client_history', {
        p_salon_id: currentSalon.id,
        p_phone: phone.trim(),
      });

      if (error) throw error;
      if (!data) return null;

      const rawHistory = data as Record<string, unknown>;
      const preferredHourRangeRaw =
        rawHistory.preferred_hour_range && typeof rawHistory.preferred_hour_range === 'object'
          ? (rawHistory.preferred_hour_range as Record<string, unknown>)
          : null;
      const serviceVisitCountsRaw =
        rawHistory.service_visit_counts && typeof rawHistory.service_visit_counts === 'object'
          ? (rawHistory.service_visit_counts as Record<string, unknown>)
          : {};

      const serviceVisitCounts = Object.entries(serviceVisitCountsRaw).reduce<Record<string, number>>((acc, [serviceId, visits]) => {
        acc[serviceId] = Number(visits ?? 0);
        return acc;
      }, {});

      return {
        clientName: String(rawHistory.clientName ?? ''),
        preferred_professional_id:
          typeof rawHistory.preferred_professional_id === 'string'
            ? rawHistory.preferred_professional_id
            : null,
        preferred_hour_range: preferredHourRangeRaw
          ? {
              from: Number(preferredHourRangeRaw.from ?? 0),
              to: Number(preferredHourRangeRaw.to ?? 0),
            }
          : null,
        total_visits: Number(rawHistory.total_visits ?? 0),
        no_show_count: Number(rawHistory.no_show_count ?? 0),
        last_visit_date:
          typeof rawHistory.last_visit_date === 'string'
            ? new Date(rawHistory.last_visit_date)
            : null,
        service_visit_counts: serviceVisitCounts,
      };
      /*

      const [{ data: apts }, { count: noShowCount }] = await Promise.all([
        supabase
        .from('appointments')
        .select('professional_id, start_time, status, service_id')
        .eq('client_id', (client as Record<string, unknown>).id as string)
        .eq('organization_id', currentSalon.organization_id)
        .filter('salon_id', 'eq', currentSalon.id)
        .eq('status', 'completed')
        .order('start_time', { ascending: false })
        .limit(20),
        supabase
          .from('appointments')
          .select('id', { count: 'exact', head: true })
          .eq('client_id', (client as Record<string, unknown>).id as string)
          .eq('organization_id', currentSalon.organization_id)
          .filter('salon_id', 'eq', currentSalon.id)
          .eq('status', 'no_show'),
      ]);

      const completed = (apts ?? []) as { professional_id: string | null; start_time: string; service_id: string | null }[];

      // Most-visited professional
      const proCount: Record<string, number> = {};
      for (const a of completed) {
        if (a.professional_id)
          proCount[a.professional_id] = (proCount[a.professional_id] ?? 0) + 1;
      }
      const preferredProId =
        Object.entries(proCount).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

      // Preferred hour range: avg ± 60 min
      const hourMins = completed.map((a) => {
        const d = new Date(a.start_time);
        return d.getHours() * 60 + d.getMinutes();
      });
      const preferredHourRange = hourMins.length
        ? (() => {
            const avg = hourMins.reduce((s, m) => s + m, 0) / hourMins.length;
            return {
              from: Math.max(0, Math.round(avg - 60)),
              to: Math.min(1439, Math.round(avg + 60)),
            };
          })()
        : null;

      const serviceVisitCounts = completed.reduce<Record<string, number>>((acc, apt) => {
        if (!apt.service_id) return acc;
        acc[apt.service_id] = (acc[apt.service_id] ?? 0) + 1;
        return acc;
      }, {});

      */
    } catch {
      return null;
    }
  }, []);

  const bookAppointment = useCallback(async (
    params: BookingParams
  ): Promise<{ success: boolean; error?: string; depositPaid?: boolean }> => {
    const currentSalon = salonRef.current;
    if (!currentSalon) return { success: false, error: 'Salón no disponible' };

    try {
      // 1. Find or create client
      const { data: clientId, error: clientError } = await supabase.rpc(
        'find_or_create_public_client',
        {
          p_salon_id: currentSalon.id,
          p_full_name: params.clientName,
          p_phone: params.clientPhone,
          p_email: params.clientEmail || null,
        }
      );
      if (clientError) throw clientError;

      // 2. Create group for multiple services
      let groupId: string | null = null;
      if (params.selectedServices.length > 1) {
        const { data: gid, error: gError } = await supabase.rpc(
          'create_public_appointment_group',
          { p_salon_id: currentSalon.id, p_client_id: clientId }
        );
        if (gError) throw gError;
        groupId = gid as string;
      }

      // 3. Book each service sequentially
      // buildLocalTimestamp: uses local clock so Chile tz is preserved when converting to UTC
      function buildLocalTimestamp(date: Date, minutes: number): string {
        const d = new Date(date);
        d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
        return d.toISOString();
      }

      // Solo se marca cuando mp-verify-deposit-and-book confirmó `approved`
      // contra la API de MercadoPago y creó la cita con deposit_paid = true.
      let verifiedDepositPaid = false;
      let currentStart = params.startMinute;
      const firstStartTime = buildLocalTimestamp(params.date, currentStart);

      for (let i = 0; i < params.selectedServices.length; i++) {
        const svc = params.selectedServices[i];
        const startTime = buildLocalTimestamp(params.date, currentStart);
        const endMin = currentStart + svc.duration_minutes;
        const endTime = buildLocalTimestamp(params.date, endMin);

        const isFirstService = i === 0;
        const depositPaid = isFirstService && !!params.mpPaymentDone && params.depositAmount > 0;

        if (depositPaid) {
          // El pago se verifica de verdad contra MercadoPago dentro de la Edge
          // Function antes de crear la cita — book_appointment_safe() ya no
          // acepta deposit_paid=true directo del cliente (ver migracion
          // 20260816090000_f7_fix_book_appointment_safe_deposit_verification).
          if (!params.mpPaymentId) throw new Error('Falta el identificador de pago de MercadoPago');
          const { data: verifyData, error: verifyError } = await supabase.functions.invoke('mp-verify-deposit-and-book', {
            body: {
              salon_id: currentSalon.id,
              professional_id: params.professionalId,
              client_id: clientId,
              service_id: svc.id,
              start_time: startTime,
              end_time: endTime,
              deposit: params.depositAmount,
              group_id: groupId,
              reservation_id: params.reservationId ?? null,
              payment_id: params.mpPaymentId,
              campaign_code: params.campaignCode || null,
            },
          });

          if (verifyError) throw verifyError;
          if (verifyData?.error) {
            const msg = verifyData.error as string;
            if (msg.includes('SLOT_TAKEN')) return { success: false, error: 'SLOT_TAKEN' };
            if (msg.includes('SLOT_RESERVED')) return { success: false, error: 'SLOT_RESERVED' };
            if (msg.includes('PHONE_LIMIT')) return { success: false, error: 'PHONE_LIMIT' };
            if (msg.includes('IP_LIMIT')) return { success: false, error: 'IP_LIMIT' };
            if (msg.includes('BOOKING_TOO_SOON')) {
              await refreshGuardrailsAndServerClock();
              return { success: false, error: 'BOOKING_TOO_SOON' };
            }
            if (msg.includes('INVALID_CAMPAIGN_CODE')) return { success: false, error: 'INVALID_CAMPAIGN_CODE' };
            if (msg.includes('CAMPAIGN_CODE_EXPIRED')) return { success: false, error: 'CAMPAIGN_CODE_EXPIRED' };
            // El pago no está `approved` en MercadoPago. Se distingue del resto
            // para no decirle al cliente que su pago fue recibido: sería la
            // misma clase de afirmación falsa que el "Abono pagado" del bug.
            if (msg.includes('no está aprobado')) return { success: false, error: 'DEPOSIT_NOT_VERIFIED' };
            throw new Error(msg);
          }
          // La Edge Function validó `approved` contra /v1/payments y creó la
          // cita con deposit_paid = true. Este es el ÚNICO punto donde el
          // cobro queda confirmado.
          verifiedDepositPaid = true;
        } else {
          const { error: bookError } = await supabase.rpc('book_public_appointment_safe', {
            p_salon_id: currentSalon.id,
            p_professional_id: params.professionalId,
            p_client_id: clientId,
            p_service_id: svc.id,
            p_start_time: startTime,
            p_end_time: endTime,
            p_deposit: isFirstService ? params.depositAmount : 0,
            p_group_id: groupId,
            p_reservation_id: isFirstService ? (params.reservationId ?? null) : null,
            p_deposit_paid: false,
            p_deposit_paid_at: null,
            p_mp_payment_id: null,
            p_campaign_code: params.campaignCode || null,
          });

          if (bookError) {
            if (bookError.message?.includes('SLOT_TAKEN')) return { success: false, error: 'SLOT_TAKEN' };
            if (bookError.message?.includes('SLOT_RESERVED')) return { success: false, error: 'SLOT_RESERVED' };
            if (bookError.message?.includes('PHONE_LIMIT')) return { success: false, error: 'PHONE_LIMIT' };
            if (bookError.message?.includes('IP_LIMIT')) return { success: false, error: 'IP_LIMIT' };
            if (bookError.message?.includes('BOOKING_TOO_SOON')) {
              await refreshGuardrailsAndServerClock();
              return { success: false, error: 'BOOKING_TOO_SOON' };
            }
            if (bookError.message?.includes('INVALID_CAMPAIGN_CODE')) return { success: false, error: 'INVALID_CAMPAIGN_CODE' };
            if (bookError.message?.includes('CAMPAIGN_CODE_EXPIRED')) return { success: false, error: 'CAMPAIGN_CODE_EXPIRED' };
            throw bookError;
          }
        }

        currentStart = endMin;
      }

      // V3 Fase 2 D5: record booking in slot_demand_stats (non-blocking)
      void supabase.rpc('upsert_slot_demand_stats', {
        p_professional_id: params.professionalId,
        p_salon_id: currentSalon.id,
        p_day_of_week: params.date.getDay(),
        p_hour_slot: params.startMinute,
        p_was_booked: true,
      });

      // Fire confirmation email (non-blocking) — look up the appointment we just created
      void (async () => {
        try {
          const { data: newApt } = await supabase
            .from('appointments')
            .select('id')
            .eq('organization_id', currentSalon.organization_id)
            .filter('salon_id', 'eq', currentSalon.id)
            .eq('client_id', clientId)
            .eq('professional_id', params.professionalId)
            .eq('start_time', firstStartTime)
            .maybeSingle();
          if (newApt?.id) {
            const { error } = await supabase.functions.invoke('send-appointment-email', {
              body: { appointmentId: newApt.id, type: 'new_appointment' },
            });
            if (error) {
              logger.error('[bookAppointment] new_appointment_notification_failed', {
                error,
                appointmentId: newApt.id,
              });
            }
          }
        } catch {
          // email is non-critical
        }
      })();

      // `depositPaid` refleja el camino REALMENTE tomado: solo es true si la
      // reserva se creó vía mp-verify-deposit-and-book, que valida contra
      // /v1/payments que el pago está `approved`. No se deriva del importe ni
      // del query string de retorno.
      return { success: true, depositPaid: verifiedDepositPaid };
    } catch (err: unknown) {
      logger.error('[bookAppointment]', err);
      return { success: false, error: 'Error al reservar. Intenta de nuevo.' };
    }
  }, [refreshGuardrailsAndServerClock]);

  /**
   * Fase 3: Add a client to the waitlist for a specific professional + date.
   * Called when `slots.length === 0` and the industry supports waitlist.
   */
  const addToWaitlist = useCallback(async (params: {
    professionalId: string | null;
    date: Date;
    clientName: string;
    clientPhone: string;
    clientEmail?: string;
    serviceIds: string[];
    notes?: string;
  }): Promise<{ success: boolean; error?: string }> => {
    const currentSalon = salonRef.current;
    if (!currentSalon) return { success: false, error: 'Salón no disponible' };

    const dateStr = [
      params.date.getFullYear(),
      String(params.date.getMonth() + 1).padStart(2, '0'),
      String(params.date.getDate()).padStart(2, '0'),
    ].join('-');

    const { error } = await supabase.from('waitlist').insert({
      salon_id: currentSalon.id,
      professional_id: params.professionalId,
      requested_date: dateStr,
      client_name: params.clientName.trim(),
      client_phone: params.clientPhone.trim(),
      client_email: params.clientEmail?.trim() || null,
      service_ids: params.serviceIds,
      notes: params.notes?.trim() || null,
    });

    if (error) {
      logger.error('[addToWaitlist]', error);
      return { success: false, error: 'No se pudo agregar a la lista de espera.' };
    }

    return { success: true };
  }, []);

  /**
   * Fase 4: Create a 30-min pre-reservation for this slot.
   * Returns the reservation UUID or null on failure.
   */
  const reserveSlot = useCallback(async (params: {
    professionalId: string;
    date: Date;
    slotStartMin: number;
    slotEndMin: number;
    clientPhone: string;
  }): Promise<string | null> => {
    const currentSalon = salonRef.current;
    if (!currentSalon) return null;
    try {
      const { data, error } = await supabase.rpc('reserve_slot_v3', {
        p_salon_id: currentSalon.id,
        p_professional_id: params.professionalId,
        p_requested_date: [
          params.date.getFullYear(),
          String(params.date.getMonth() + 1).padStart(2, '0'),
          String(params.date.getDate()).padStart(2, '0'),
        ].join('-'),
        p_slot_start_min: params.slotStartMin,
        p_slot_end_min: params.slotEndMin,
        p_client_phone: params.clientPhone.trim(),
      });
      if (error) { logger.warn('[reserveSlot]', error); return null; }
      return data as string;
    } catch {
      return null;
    }
  }, []);

  /**
   * Fase 4: Release a pre-reservation (client went back or cancelled).
   */
  const releaseSlotReservation = useCallback(async (reservationId: string): Promise<void> => {
    try {
      await supabase.rpc('release_slot_reservation', { p_reservation_id: reservationId });
    } catch {
      // non-critical
    }
  }, []);

  return {
    salon,
    services,
    professionals,
    serviceBuckets,
    loading,
    error,
    slotsStale,
    setSlotsStale,
    getAvailableSlots,
    lookupClientHistory,
    bookAppointment,
    addToWaitlist,
    reserveSlot,
    releaseSlotReservation,
  };
}
